import React from 'react';
import { AppIdea } from '@/hooks/app-library';
import { TextContentContext } from '@/components/text-content-lightbox';
import PersistentFavoriteButton from '@/components/ui/PersistentFavoriteButton';
import GlassPill from '@/components/ui/GlassPill';
import { Progress } from '@/components/ui/progress';
import { useAppIdeaFavorites } from '@/hooks/app-library';
import { capitalize, cn } from '@/lib/utils';

interface AppIdeaContentHeaderProps {
  idea: AppIdea;
  context: TextContentContext<AppIdea>;
}

export const AppIdeaContentHeader: React.FC<AppIdeaContentHeaderProps> = ({ idea, context }) => {
  const { handleToggleFavorite, isAppIdeaFavorited, getAppIdeaFavoriteCount } = useAppIdeaFavorites();
  
  const isFavorited = isAppIdeaFavorited(idea.id);
  const favoriteCount = getAppIdeaFavoriteCount(idea.id);

  // App Idea development progress calculations
  const DEVELOPMENT_THRESHOLD = 100;
  const progressPercentage = Math.min((favoriteCount / DEVELOPMENT_THRESHOLD) * 100, 100);
  const likesToGo = Math.max(DEVELOPMENT_THRESHOLD - favoriteCount, 0);
  const isReadyForDevelopment = favoriteCount >= DEVELOPMENT_THRESHOLD;

  return (
    <div className="space-y-4 mb-6">
      {/* Modern Glass Pill Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 w-full">
        {/* Left side - Page Number + Favorites Button */}
        <div className="flex items-center gap-2">
          {/* Page Number - FIRST */}
          {context.totalItems > 1 && (
            <GlassPill 
              type={`${context.currentIndex + 1} of ${context.totalItems}`}
              variant="default"
              className="text-sm font-medium"
            />
          )}
          
          {/* If only one item, still show page number for consistency */}
          {context.totalItems <= 1 && (
            <GlassPill 
              type="1 of 1"
              variant="default"
              className="text-sm font-medium"
            />
          )}
          
          {/* Favorites Button - SECOND */}
          <PersistentFavoriteButton
            itemId={idea.id}
            onToggleFavorite={handleToggleFavorite}
            isFavorite_prop={isFavorited}
            favoriteCount_prop={favoriteCount || 0}
          />
        </div>
        
        {/* Right side - Category + Date */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Category - FIRST on right */}
          {idea.category && (
            <GlassPill 
              type={capitalize(idea.category)}
              variant="amber"
              className="text-sm font-medium"
            />
          )}
          
          {/* Creation Date - SECOND on right */}
          {idea.created_at && (
            <GlassPill 
              type={new Date(idea.created_at).toLocaleDateString()}
              variant="blue"
              className="text-sm font-medium"
            />
          )}
        </div>
      </div>
      
      {/* Title */}
      <h1 className="text-xl md:text-2xl font-semibold text-foreground leading-tight pr-2 break-words">
        {idea.name}
      </h1>
      
      {/* Description */}
      {idea.description && (
        <p className="text-muted-foreground leading-relaxed">
          {idea.description}
        </p>
      )}
      
      {/* App Idea Development Progress Bar */}
      <div className="space-y-3 p-4 bg-gradient-to-r from-orange-50/60 to-amber-50/60 dark:from-orange-950/20 dark:to-amber-950/20 rounded-xl border border-orange-200/50 dark:border-orange-800/30">
        <div className="flex items-center justify-between">
          <span className={cn(
            "text-sm font-medium",
            isReadyForDevelopment ? "text-green-600 dark:text-green-400" : "text-orange-600 dark:text-orange-400"
          )}>
            {isReadyForDevelopment ? "Ready for Development! 🎉" : "Proposal Progress"}
          </span>
          <span className={cn(
            "text-sm font-bold",
            isReadyForDevelopment ? "text-green-600 dark:text-green-400" : "text-orange-600 dark:text-orange-400"
          )}>
            {favoriteCount}/{DEVELOPMENT_THRESHOLD} likes
          </span>
        </div>
        <Progress 
          value={progressPercentage} 
          className="h-3"
          indicatorClassName={cn(
            "transition-all duration-500",
            isReadyForDevelopment 
              ? "bg-gradient-to-r from-green-500 to-emerald-500" 
              : "bg-gradient-to-r from-orange-400 to-amber-500"
          )}
        />
        {!isReadyForDevelopment && likesToGo > 0 && (
          <p className="text-sm text-muted-foreground">
            {likesToGo} more {likesToGo === 1 ? 'like' : 'likes'} needed to start development
          </p>
        )}
        {isReadyForDevelopment && (
          <p className="text-sm text-green-600 dark:text-green-400 font-semibold">
            This app idea will be developed for the community!
          </p>
        )}
      </div>
    </div>
  );
};
